// Turning a typed price ("150", "150.50") into the wire's integer minor units, without a float
// (spec/06 §1). The Sell and F&B menu screens both parse a price the same way, so it lives here.
import type { Money } from '@clavian/api-client';
import { minorUnitDigits } from '@clavian/i18n';

/**
 * A decimal string in the currency's major unit → integer minor units, or `null` if it is not a
 * valid, non-negative amount with at most the currency's fraction digits. No division: the string is
 * split and padded, so "150.5" in INR is 15050, never a rounded float.
 */
export function majorToMinor(input: string, currency: string): number | null {
  const trimmed = input.trim();
  const digits = minorUnitDigits(currency);
  const match = /^(\d+)(?:\.(\d+))?$/.exec(trimmed);
  if (match === null) return null;
  const whole = match[1] ?? '';
  const fraction = match[2] ?? '';
  if (fraction.length > digits) return null;
  const padded = fraction.padEnd(digits, '0');
  const minor = Number(whole) * 10 ** digits + Number(padded || '0');
  return Number.isSafeInteger(minor) ? minor : null;
}

/** A price field's value → a `Money`, or `null` when it is not a valid amount. */
export function moneyFrom(input: string, currency: string): Money | null {
  const amount = majorToMinor(input, currency);
  return amount === null ? null : { amount, currency };
}

/**
 * Integer minor units → the decimal string a price field shows (15050 in INR → "150.50", 12000 →
 * "120"), the inverse of `majorToMinor`. No division: the digits are split at the currency's fraction
 * width, so JPY (0 digits) and KWD (3) come out right; a whole amount drops its zeros, any other keeps
 * every fraction digit.
 */
export function minorToMajor(amount: number, currency: string): string {
  const digits = minorUnitDigits(currency);
  const sign = amount < 0 ? '-' : '';
  const text = String(Math.abs(Math.trunc(amount))).padStart(digits + 1, '0');
  if (digits === 0) return `${sign}${text}`;
  const whole = text.slice(0, -digits);
  const fraction = text.slice(-digits);
  return /^0+$/.test(fraction) ? `${sign}${whole}` : `${sign}${whole}.${fraction}`;
}

/**
 * The round amounts a counter is handed, per currency, in major units — the steps quick tender rounds
 * up to. Clavian launches in India, so INR is the one tuned table (the ₹50, ₹100, ₹500 and ₹2,000
 * notes); every other currency falls back to `DEFAULT_CASH_STEPS` until its market is added here — or,
 * once venues need to tune it, moved into the settings registry with this table as the default
 * (spec/03 §9). Adding a market is a new entry, not a change to the arithmetic.
 */
const CASH_STEPS: Readonly<Record<string, readonly number[]>> = {
  INR: [50, 100, 500, 2_000],
};

/** Steps for a currency with no table of its own yet: a sensible generic ladder of round amounts. */
const DEFAULT_CASH_STEPS: readonly number[] = [10, 50, 100];

/** The quick-tender steps for a currency, in major units. */
export function cashStepsFor(currency: string): readonly number[] {
  return CASH_STEPS[currency.toUpperCase()] ?? DEFAULT_CASH_STEPS;
}

/**
 * Quick tender for cash (spec/13 §4): the amount due exactly, then the next round amounts a player is
 * likely to hand over — each step rounded up, above the amount due, distinct and ascending, at most
 * three. All integer minor units; nothing is charged from this, it only fills "cash tendered".
 */
export function cashSuggestions(dueMinor: number, currency: string): readonly number[] {
  if (!Number.isSafeInteger(dueMinor) || dueMinor <= 0) return [];
  const unit = 10 ** minorUnitDigits(currency);
  const rounded = cashStepsFor(currency).map((step) => Math.ceil(dueMinor / (step * unit)) * step * unit).filter((value) => value > dueMinor);
  return [...new Set(rounded)].sort((a, b) => a - b).slice(0, 3);
}

/**
 * The change to hand back for a cash tender, in minor units: what was handed over less what this
 * tender pays, or `null` when too little was handed over. A preview only — the server's `change` on
 * the recorded payment is the figure of record (F-POS-12).
 */
export function changeDue(tenderedMinor: number, payingMinor: number): number | null {
  if (!Number.isSafeInteger(tenderedMinor) || !Number.isSafeInteger(payingMinor)) return null;
  return tenderedMinor >= payingMinor ? tenderedMinor - payingMinor : null;
}
